import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset demonstrating the reactive condition (reaction) system.
 *
 * A quadratic f(x) = a(x-h)² + k with buttons to toggle the sign of a.
 * Reactions respond to parameter changes:
 *
 * 1. **Concavity reaction**: Curve and vertex color change based on sign of `a`
 *    - a > 0 → green (opens up, minimum)
 *    - a < 0 → red (opens down, maximum)
 *    - a == 0 → neutral (degenerate, no curvature)
 *
 * 2. **Discriminant reaction**: Roots visibility based on b² - 4ac
 *    - discriminant >= 0 → roots visible (teal)
 *    - discriminant < 0 → roots hidden
 *
 * 3. **Vertex label reaction**: Label text changes to "Minimum" / "Maximum"
 *    - a > 0 → "Minimum"
 *    - a < 0 → "Maximum"
 *    - a == 0 → hidden
 */
export const TEST_CONDITIONAL_PRESET: GraphpadWidgetConfig = {
  id: 'test-conditional',
  type: 'graphpad',
  params: {
    f: {
      type: 'quadratic-vertex',
      params: { a: 1, h: 0, k: -2 },
    },
    'f-roots': { type: 'roots', params: { of: 'f' } },
    'f-extrema': { type: 'extrema', params: { of: 'f' } },

    'label-vertex': {
      type: 'label',
      params: {
        text: 'Minimum',
        of: 'f-extrema',
      },
    },
  },
  display: {
    canvas: { color: 'neutral-0' },
    coordinateSystem: {
      boundingbox: { xStart: -10, xEnd: 10, yStart: -10, yEnd: 10 },
      grid: { type: 'lines', visible: false },
    },
    elements: {
      'f-extrema': { point: { color: 'green' } },
    },
  },
  behavior: {
    controls: [
      {
        id: 'btn-a-positive',
        type: WIDGET_CONTROL_TYPE.Button,
        label: 'a > 0',
        position: 'bottom-left',
        group: 'sign-of-a',
        trigger: {
          action: 'UPDATE_PARAM',
          payload: { param: 'f.a', value: 1 },
          transition: { duration: 500, easing: 'easeInOut' },
        },
      },
      {
        id: 'btn-a-zero',
        type: WIDGET_CONTROL_TYPE.Button,
        label: 'a = 0',
        position: 'bottom-left',
        group: 'sign-of-a',
        trigger: {
          action: 'UPDATE_PARAM',
          payload: { param: 'f.a', value: 0 },
          transition: { duration: 500, easing: 'easeInOut' },
        },
      },
      {
        id: 'btn-a-negative',
        type: WIDGET_CONTROL_TYPE.Button,
        label: 'a < 0',
        position: 'bottom-left',
        group: 'sign-of-a',
        trigger: {
          action: 'UPDATE_PARAM',
          payload: { param: 'f.a', value: -1 },
          transition: { duration: 500, easing: 'easeInOut' },
        },
      },
    ],
    reactions: [
      {
        match: [
          {
            when: 'f.a > 0',
            dispatch: {
              action: 'PATCH_DISPLAY',
              payload: {
                f: { curve: { color: 'green' } },
                'f-translate': { point: { color: 'green' } },
                'f-extrema': { point: { color: 'green' } },
              },
            },
          },
          {
            when: 'f.a < 0',
            dispatch: {
              action: 'PATCH_DISPLAY',
              payload: {
                f: { curve: { color: 'red' } },
                'f-extrema': { point: { color: 'red' } },
              },
            },
          },
          {
            else: true,
            dispatch: {
              action: 'PATCH_DISPLAY',
              payload: {
                f: { curve: { color: 'neutral' } },
                'f-extrema': { point: { color: 'neutral' } },
              },
            },
          },
        ],
      },
      // Reaction 3: Vertex label text
      {
        match: [
          {
            when: 'f.a > 0',
            dispatch: {
              action: 'PATCH_DISPLAY',
              payload: {
                'label-vertex': {
                  label: { text: 'Minimum', color: 'green', offset: [0, -20], variant: 'secondary' },
                  visible: true,
                },
              },
            },
          },
          {
            when: 'f.a < 0',
            dispatch: {
              action: 'PATCH_DISPLAY',
              payload: {
                'label-vertex': {
                  label: { text: 'Maximum', color: 'red', offset: [0, 20], variant: 'secondary' },
                  visible: true,
                },
              },
            },
          },
          {
            else: true,
            dispatch: {
              action: 'PATCH_DISPLAY',
              payload: { 'label-vertex': { visible: false } },
            },
          },
        ],
      },
    ],
  },
};
